import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabase";
import "./Navbar.css";
import logo from "../images/download.svg";
import profileIcon from "../images/profile-icon.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMoneyBill, faTimes } from "@fortawesome/free-solid-svg-icons";

// Format a "YYYY-MM-DD" expiry date as "28 May" without timezone shifting.
const formatExpiryDate = (dateString) => {
  const [year, month, day] = dateString.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

// Drop trailing ".00" so "50.00" reads as "50" but "50.50" is preserved.
const formatCreditAmount = (amount) =>
  Number.isInteger(amount) ? String(amount) : amount.toFixed(2);

// Credits expiring within this many days are flagged in red.
const EXPIRY_WARNING_DAYS = 7;

const isExpiringSoon = (dateString) => {
  const [year, month, day] = dateString.split("-").map(Number);
  const expiry = new Date(year, month - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
  return diffDays <= EXPIRY_WARNING_DAYS;
};

const Navbar = ({
  selectedSport,
  selectedBookingType,
  onBookingTypeSelect,
  onProfileClick,
  isProfileActive,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollDirection, setScrollDirection] = useState("down");
  const [userCredits, setUserCredits] = useState(0.00);
  const [creditExpiry, setCreditExpiry] = useState(null);
  const [showCreditInfoPopup, setShowCreditInfoPopup] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // The title rolls to "PadelDepot" for every Padel flow, not just court
    // booking: App passes "Padel Coach" and "Padel Activities" too, which an
    // exact "Padel" match missed and left branded as PickleDepot.
    const newDirection = String(selectedSport || "").startsWith("Padel") ? "up" : "down";
    setScrollDirection(newDirection);
  }, [selectedSport]);

  // Fetch and subscribe to user credits with error handling and fallback
  useEffect(() => {
    let subscription = null;

    const fetchAndSubscribeCredits = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          console.error("Navbar.js: No authenticated user found");
          setUserCredits(0.00);
          return;
        }

        // Initial fetch
        const fetchCredits = async () => {
          const { data, error } = await supabase
            .from('credits')
            .select('balance')
            .eq('user_id', user.id)
            .single();

          if (error && error.code !== 'PGRST116') throw error; // PGRST116: no rows found
          const credits = data?.balance || 0.00;
          setUserCredits(credits);
        };

        // Next batch of credits to expire (soonest expiry date + amount on it).
        // credit_transactions has no per-user RLS read policy, so this goes
        // through the get_next_credit_expiry SECURITY DEFINER RPC.
        const fetchCreditExpiry = async () => {
          const { data, error } = await supabase.rpc('get_next_credit_expiry');
          if (error) {
            console.error('Navbar.js: Error fetching credit expiry:', error);
            return;
          }
          const next = data?.[0];
          setCreditExpiry(next ? { amount: Number(next.expiring_amount), date: next.expires_on } : null);
        };

        await Promise.all([fetchCredits(), fetchCreditExpiry()]);

        // Subscribe to real-time updates
        subscription = supabase
          .channel('credits-changes')
          .on('postgres_changes',
            { event: 'UPDATE', schema: 'public', table: 'credits', filter: `user_id=eq.${user.id}` },
            (payload) => {
              console.log('Navbar.js: Received real-time update for credits:', payload);
              setUserCredits(payload.new.balance);
              fetchCreditExpiry();
            })
          .subscribe((status, err) => {
            if (status === 'SUBSCRIBED') {
              console.log('Navbar.js: Real-time subscription established for credits');
            } else if (status === 'CHANNEL_ERROR') {
              console.error('Navbar.js: Channel error in subscription:', err?.message);
            } else if (status === 'TIMED_OUT') {
              console.error('Navbar.js: Real-time subscription timed out');
            } else if (status === 'CLOSED') {
              console.log('Navbar.js: Real-time subscription closed');
            }
          });

      } catch (error) {
        console.error("Navbar.js: Error setting up credits subscription:", error);
        setUserCredits(0.00);
      }
    };

    fetchAndSubscribeCredits();

    // Cleanup on unmount
    return () => {
      if (subscription) {
        supabase.removeChannel(subscription);
        console.log('Navbar.js: Unsubscribed from credits-changes channel');
      }
    };
  }, []);

  const handleLogoClick = () => {
    const defaultState = {
      selectedBookingType: "Bookings",
      selectedProfile: false,
      selectedSport: null,
      selectedDate: null,
      selectedCourtType: null,
      selectedCourt: null,
      selectedTimeslots: [],
      selectedMembership: null,
      membershipPrice: null,
    };
    navigate("/", { state: defaultState });
    if (window.innerWidth <= 1024) setIsMenuOpen(false);
  };

  const handleMembershipClick = () => {
    const resetState = {
      selectedBookingType: null,
      selectedProfile: false,
      selectedSport: null,
      selectedDate: null,
      selectedCourtType: null,
      selectedCourt: null,
      selectedTimeslots: [],
      selectedMembership: null,
      membershipPrice: null,
    };
    navigate("/membership-upgrade", { state: resetState });
    if (window.innerWidth <= 1024) setIsMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error("Navbar.js: Error signing out:", error);
        alert("Failed to sign out. Please try again.");
        return;
      }
      navigate("/login", {
        state: { logoutSuccess: true },
        replace: true,
      });
      if (window.innerWidth <= 1024) setIsMenuOpen(false);
    } catch (error) {
      console.error("Navbar.js: Unexpected error during sign out:", error);
      alert("An unexpected error occurred. Please try again.");
    }
  };

  const handleCreditsClick = () => {
    setShowCreditInfoPopup(true);
    if (window.innerWidth <= 1024) setIsMenuOpen(false);
  };

  // Fall back to plain navigation when a page renders <Navbar /> without the
  // callback props, so a missing prop can't crash the click handler.
  const handleBookingTypeClick = (type) => {
    if (onBookingTypeSelect) onBookingTypeSelect(type);
    else navigate("/", { state: { selectedBookingType: type } });
    if (window.innerWidth <= 1024) setIsMenuOpen(false);
  };

  const handleProfileIconClick = () => {
    if (onProfileClick) onProfileClick();
    else navigate("/profile", { state: { selectedProfile: true } });
    if (window.innerWidth <= 1024) setIsMenuOpen(false);
  };

  const handleCloseCreditInfoPopup = () => {
    setShowCreditInfoPopup(false);
  };

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <div
          className="logo-container"
          onClick={handleLogoClick}
          style={{ cursor: "pointer" }}
        >
          <img src={logo} alt="PickleDepot/PadelDepot Logo" className="logo" />
          <span className="logo-text">
            <span className="logo-text-wrapper">
              <span className={`logo-text-prefix ${scrollDirection}`}>
                <span className="logo-text-prefix-item">Pickle</span>
                <span className="logo-text-prefix-item">Padel</span>
                <span className="logo-text-prefix-item">Pickle</span>
              </span>
            </span>
            <span className="logo-text-suffix">Depot</span>
          </span>
        </div>

        <div className={`navbar-menu-items ${isMenuOpen ? "active" : ""}`}>
          <button
            className={`navbar-menu-item ${
              !isProfileActive && selectedBookingType === "Bookings"
                ? "active"
                : ""
            }`}
            onClick={() => handleBookingTypeClick("Bookings")}
          >
            Make Booking
          </button>
          <button
            className={`navbar-menu-item ${
              !isProfileActive &&
              window.location.pathname === "/membership-upgrade"
                ? "active"
                : ""
            }`}
            onClick={handleMembershipClick}
          >
            Membership
          </button>
          {window.innerWidth <= 1024 && (
            <>
              <div
                className="navbar-credits mobile-credits"
                onClick={handleCreditsClick}
                style={{ cursor: "pointer" }}
              >
                <span className="credits-balance">
                  <FontAwesomeIcon icon={faMoneyBill} /> {userCredits.toFixed(2)}
                </span>
                {creditExpiry && creditExpiry.amount > 0 && (
                  <span className={`credits-expiry${isExpiringSoon(creditExpiry.date) ? " expiring-soon" : ""}`}>
                    {formatCreditAmount(creditExpiry.amount)} credits expire on{" "}
                    {formatExpiryDate(creditExpiry.date)}
                  </span>
                )}
              </div>
              <button className="navbar-menu-item" onClick={handleLogout}>
                Logout
              </button>
            </>
          )}
        </div>

        <div className="navbar-right">
          <div
            className={`profile-icon mobile-profile-icon ${
              isProfileActive ? "active" : ""
            }`}
            onClick={handleProfileIconClick}
          >
            <img
              src={profileIcon}
              alt="Profile"
              style={{ width: "100%", height: "100%" }}
            />
          </div>
          <div className="hamburger" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            ☰
          </div>
          <div
            className="navbar-credits desktop-credits"
            onClick={handleCreditsClick}
            style={{ cursor: "pointer" }}
          >
            <span className="credits-balance">
              <FontAwesomeIcon icon={faMoneyBill} /> {userCredits.toFixed(2)}
            </span>
            {creditExpiry && creditExpiry.amount > 0 && (
              <span className={`credits-expiry${isExpiringSoon(creditExpiry.date) ? " expiring-soon" : ""}`}>
                {formatCreditAmount(creditExpiry.amount)} credits expire on{" "}
                {formatExpiryDate(creditExpiry.date)}
              </span>
            )}
          </div>
          <div
            className={`profile-icon desktop-profile-icon ${
              isProfileActive ? "active" : ""
            }`}
            onClick={handleProfileIconClick}
          >
            <img
              src={profileIcon}
              alt="Profile"
              style={{ width: "100%", height: "100%" }}
            />
          </div>
          <button className="logout-button" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>

      {showCreditInfoPopup && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Credits Information</h2>
            <p>
              Credits can be used to pay for bookings. They are issued as refunds or manually by admins and can be applied during checkout. Each credit is worth RM 1.00 in value. Check your balance above and toggle the "Use Credits for Payment" option to use them during checkout!
            </p>
            <button
              className="modal-close-button"
              onClick={handleCloseCreditInfoPopup}
            >
              <FontAwesomeIcon icon={faTimes} /> Got It
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;